import { useCallback, useLayoutEffect } from 'react';

import {
  AUTO_ZOOM_DELAY_LOOPS,
  DEFAULT_ZOOM,
  DESIGN_COUNTERTOP_ACTIONS_HEIGHT,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { autoZoomPanToFit } from '~/core/helpers';
import useWindowSize from '~/core/hooks/useWindowSize';
import { DrawingShape } from '~/core/models';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppSelector } from '~/redux/hooks';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { selectIsManualZoom } from '~/redux/slices/toolbarCanvas';

const isCanvasValid = (canvas: fabric.Canvas | null): canvas is fabric.Canvas =>
  canvas !== null &&
  canvas.getWidth !== undefined &&
  canvas.getHeight !== undefined;

const setDefaultZoom = (canvas: fabric.Canvas): void => {
  try {
    canvas.setZoom(DEFAULT_ZOOM);
  } catch (error) {
    console.warn('Error setting default zoom:', error);
  }
};

const applyAutoZoom = (canvas: fabric.Canvas): void => {
  if (!canvas || !canvas.getWidth() || !canvas.getHeight()) {
    return;
  }

  try {
    autoZoomPanToFit(canvas, true, true);

    const currentZoom = canvas.getZoom();

    if (!currentZoom || currentZoom === 0 || !isFinite(currentZoom)) {
      canvas.setZoom(DEFAULT_ZOOM);
    }
  } catch (error) {
    console.warn('Error applying auto zoom:', error);
  }

  if (canvas && canvas.renderAll) {
    canvas.renderAll();
  }
};

const createZoomLoop = (
  canvas: fabric.Canvas,
  drawingShapes: DrawingShape[],
  initialCountDown: number
) => {
  let countDown = initialCountDown;

  const zoomLoop = (): void => {
    if (!isCanvasValid(canvas)) {
      return;
    }

    try {
      const shouldApplyZoom =
        countDown === 0 ||
        drawingTool.allShapesAndAppliancesAreDrawn(canvas, drawingShapes);

      if (shouldApplyZoom) {
        applyAutoZoom(canvas);

        return;
      }

      countDown -= 1;
      requestAnimationFrame(zoomLoop);
    } catch (error) {
      console.warn('Error in zoom loop:', error);
    }
  };

  return zoomLoop;
};

const useFabricCanvasWindow = (width: number) => {
  const canvas = useAppSelector(selectFabricCanvas);
  const isManualZoom = useAppSelector(selectIsManualZoom);
  const [, windowHeight] = useWindowSize();

  const {
    getters: { drawingShapes, areThereShapesInTheDrawingArea },
  } = useDrawingShapeContext();
  const { drawingShapesRef } = useFabricCanvasContext();

  const tryZooming = useCallback(() => {
    if (!isCanvasValid(canvas)) {
      return;
    }

    if (!areThereShapesInTheDrawingArea) {
      setDefaultZoom(canvas);

      return;
    }

    const zoomLoop = createZoomLoop(
      canvas,
      drawingShapes,
      AUTO_ZOOM_DELAY_LOOPS
    );

    requestAnimationFrame(zoomLoop);
  }, [areThereShapesInTheDrawingArea, canvas, drawingShapes]);

  // container dimensions change (resize window, collapse sidebars, etc)
  useLayoutEffect(() => {
    if (windowHeight && canvas && canvas.setWidth && canvas.setHeight) {
      try {
        canvas.setWidth(width);
        canvas.setHeight(0.7 * windowHeight - DESIGN_COUNTERTOP_ACTIONS_HEIGHT);
        autoZoomPanToFit(canvas, true, true);
      } catch (error) {
        console.warn('Error resizing canvas:', error);
      }
    }
  }, [width, windowHeight, canvas]);

  // auto zoom and pan if more than one shape, reset zoom if empty canvas
  useLayoutEffect(() => {
    if (drawingShapes && canvas && !isManualZoom) {
      tryZooming();
    }
  }, [
    canvas,
    isManualZoom,
    drawingShapes,
    drawingShapesRef,
    areThereShapesInTheDrawingArea,
    tryZooming,
  ]);

  return {
    windowHeight,
  };
};

export default useFabricCanvasWindow;
